import React, { useState, useMemo } from "react";
import { useApp } from "../context/AppContext";
import { CATEGORIES, CATEGORY_COLORS } from "../data/mockData";
import TransactionModal from "./TransactionModal";
import {
  Search, Plus, ArrowUpRight, ArrowDownRight,
  Pencil, Trash2, ChevronUp, ChevronDown, Download, Filter
} from "lucide-react";

export default function Transactions() {
  const { state, dispatch } = useApp();
  const isAdmin = state.role === "admin";
  const [modal, setModal] = useState(null);
  const [filters, setFilters] = useState({ search: "", category: "all", type: "all", sort: "date-desc" });

  const filtered = useMemo(() => {
    let txns = [...state.transactions];
    if (filters.search) {
      const q = filters.search.toLowerCase();
      txns = txns.filter((t) => t.description.toLowerCase().includes(q) || t.category.toLowerCase().includes(q));
    }
    if (filters.category !== "all") txns = txns.filter((t) => t.category === filters.category);
    if (filters.type !== "all") txns = txns.filter((t) => t.type === filters.type);
    txns.sort((a, b) => {
      if (filters.sort === "date-desc") return new Date(b.date) - new Date(a.date);
      if (filters.sort === "date-asc") return new Date(a.date) - new Date(b.date);
      if (filters.sort === "amount-desc") return b.amount - a.amount;
      if (filters.sort === "amount-asc") return a.amount - b.amount;
      return 0;
    });
    return txns;
  }, [state.transactions, filters]);

  const exportCSV = () => {
    const rows = [["Date", "Description", "Category", "Type", "Amount"],
    ...filtered.map((t) => [t.date, t.description, t.category, t.type, t.amount])];
    const csv = rows.map((r) => r.join(",")).join("\n");
    const a = document.createElement("a");
    a.href = "data:text/csv," + encodeURIComponent(csv);
    a.download = "transactions.csv";
    a.click();
  };

  const selectCls = "bg-bg-card border border-border rounded-xl px-3 py-2 text-sm font-body text-white/70 focus:outline-none focus:border-accent-cyan/40 transition-colors";

  return (
    <div className="space-y-5">
      {modal && (
        <TransactionModal
          transaction={modal === "add" ? null : modal}
          onClose={() => setModal(null)}
        />
      )}

      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-display font-bold text-2xl text-white mb-1">Transactions</h2>
          <p className="text-sm font-body text-white/30">{filtered.length} records found</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={exportCSV}
            className="flex items-center gap-2 px-3 py-2 rounded-xl border border-border text-white/40 hover:text-white text-sm font-body transition-colors">
            <Download size={14} /> Export
          </button>
          {isAdmin && (
            <button onClick={() => setModal("add")}
              className="flex items-center gap-2 px-4 py-2 rounded-xl bg-accent-cyan text-bg-primary text-sm font-body font-medium shadow-lg shadow-accent-cyan/20 hover:bg-accent-cyan/90 transition-all">
              <Plus size={15} /> Add
            </button>
          )}
        </div>
      </div>

      <div className="bg-bg-card border border-border rounded-2xl p-4">
        <div className="flex flex-wrap gap-3">
          <div className="flex-1 min-w-48 relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/20" />
            <input
              className="w-full bg-bg-primary border border-border rounded-xl pl-9 pr-3 py-2 text-sm font-body text-white placeholder-white/20 focus:outline-none focus:border-accent-cyan/40 transition-colors"
              placeholder="Search transactions..."
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
            />
          </div>
          <select className={selectCls} value={filters.category} onChange={(e) => setFilters({ ...filters, category: e.target.value })}>
            <option value="all">All Categories</option>
            {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <select className={selectCls} value={filters.type} onChange={(e) => setFilters({ ...filters, type: e.target.value })}>
            <option value="all">All Types</option>
            <option value="income">Income</option>
            <option value="expense">Expense</option>
          </select>
          <select className={selectCls} value={filters.sort} onChange={(e) => setFilters({ ...filters, sort: e.target.value })}>
            <option value="date-desc">Newest First</option>
            <option value="date-asc">Oldest First</option>
            <option value="amount-desc">Highest Amount</option>
            <option value="amount-asc">Lowest Amount</option>
          </select>
        </div>
      </div>

      <div className="bg-bg-card border border-border rounded-2xl overflow-hidden">
        {filtered.length === 0 ? (
          <div className="text-center py-16">
            <Filter size={32} className="text-white/10 mx-auto mb-3" />
            <p className="font-body text-white/20">No transactions match your filters</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border">
                  {["Date", "Description", "Category", "Type", "Amount", ...(isAdmin ? ["Actions"] : [])].map((h) => (
                    <th key={h} className="text-left px-4 py-3 text-xs font-body text-white/25 uppercase tracking-widest font-medium">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((t, i) => (
                  <tr key={t.id}
                    style={{ animationDelay: `${i * 30}ms` }}
                    className="border-b border-border/50 last:border-0 hover:bg-bg-hover transition-colors animate-fade-in group">
                    <td className="px-4 py-3 text-sm font-mono text-white/40">{t.date}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 ${t.type === "income" ? "bg-accent-green/10" : "bg-accent-red/10"
                          }`}>
                          {t.type === "income"
                            ? <ArrowUpRight size={12} className="text-accent-green" />
                            : <ArrowDownRight size={12} className="text-accent-red" />}
                        </div>
                        <span className="text-sm font-body text-white/80">{t.description}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-xs font-body px-2 py-1 rounded-lg border"
                        style={{ color: CATEGORY_COLORS[t.category], borderColor: CATEGORY_COLORS[t.category] + "30", backgroundColor: CATEGORY_COLORS[t.category] + "10" }}>
                        {t.category}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-xs font-body capitalize px-2 py-1 rounded-lg ${t.type === "income" ? "text-accent-green bg-accent-green/10" : "text-accent-red bg-accent-red/10"
                        }`}>{t.type}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`font-mono text-sm font-medium ${t.type === "income" ? "text-accent-green" : "text-accent-red"}`}>
                        {t.type === "income" ? "+" : "-"}${t.amount.toLocaleString()}
                      </span>
                    </td>
                    {isAdmin && (
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button onClick={() => setModal(t)}
                            className="w-7 h-7 rounded-lg bg-bg-primary border border-border flex items-center justify-center text-white/40 hover:text-accent-cyan hover:border-accent-cyan/30 transition-colors">
                            <Pencil size={12} />
                          </button>
                          <button onClick={() => dispatch({ type: "DELETE_TRANSACTION", payload: t.id })}
                            className="w-7 h-7 rounded-lg bg-bg-primary border border-border flex items-center justify-center text-white/40 hover:text-accent-red hover:border-accent-red/30 transition-colors">
                            <Trash2 size={12} />
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
